/**
 * @file: 获取cube数据，用于复制和提交, 复制粘贴是虽然数据中会有id，但在新增过程中会被干掉
 * @author: xiaolong.huang@dmall.com
 */

import Utils from '../utils/util';
import { isSketchpad, isShelf, isFreezer, isPartition, isGoods, isBafflePlate, isTextarea } from './cubeInfo';
import { findByCubeId } from './baseQuery';

// 画布
function getSketchpadData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        cubes: []
    };

    for (let copyKey in result) {
        if (copyKey === 'cubes') {
            continue;
        }

        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    if (!cube.cubes) {
        return;
    }

    cube.cubes.forEach((child: FreeObjMap) => {
        if (isShelf(child.id)) {
            result.cubes.push(getShelfData(child.id));
        }
        else if (isFreezer(child.id)) {
            result.cubes.push(getShelfData(child.id));
        }
        else if (isPartition(child.id)) {
            result.cubes.push(getPartitionData(child.id));
        }
        else if (isGoods(child.id)) {
            result.cubes.push(getGoodsData(child.id));
        }
        else if (isBafflePlate(child.id)) {
            result.cubes.push(getBaffleplateData(child.id));
        }
        else if (isTextarea(child.id)) {
            result.cubes.push(getTextareaData(child.id));
        }
    });

    return result;
}

// 货架
function getShelfData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        parentCubeData: {},
        cubes: []
    };

    for (let copyKey in result) {
        if (copyKey === 'cubes') {
            continue;
        }

        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    if (!cube.cubes) {
        return;
    }

    cube.cubes.forEach((child: FreeObjMap) => {
        result.cubes.push(getPartitionData(child.id));
    });

    return result;
}

// 层板
function getPartitionData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        cubes: []
    };

    for (let copyKey in result) {
        if (copyKey === 'cubes') {
            continue;
        }

        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    if (!cube.cubes) {
        return;
    }

    cube.cubes.forEach((child: FreeObjMap) => {
        if (isGoods(child.id)) {
            result.cubes.push(getGoodsData(child.id));
        }
        else if (isBafflePlate(child.id)) {
            result.cubes.push(getBaffleplateData(child.id));
        }
        else if (isTextarea(child.id)) {
            result.cubes.push(getTextareaData(child.id));
        }
    });

    return result;
}

// 商品
function getGoodsData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        posDefaultValue: {},
        goods: []
    };

    for (let copyKey in result) {
        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    return result;
}

// 文本框
function getTextareaData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        posDefaultValue: {},
        goods: []
    };

    for (let copyKey in result) {
        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    return result;
}

// 隔板
function getBaffleplateData(cubeId: CubeId): CubeTreeData {
    let cube: CubeNode = findByCubeId(cubeId);
    let result: CubeTreeData = {
        id: '',
        type: '',
        moduleName: '',
        tplName: '',
        cubeName: '',
        desc: '',
        frontCubeData: {},
        cubeData: {},
        posDefaultValue: {},
        goods: []
    };

    for (let copyKey in result) {
        if (Utils.isArray(cube[copyKey]) || Utils.isObject(cube[copyKey])) {
            result[copyKey] = Utils.deepCopy(cube[copyKey]);
            continue;
        }

        result[copyKey] = cube[copyKey];
    }

    return result;
}

function getCubeTreeData(cubeId: CubeId): CubeTreeData|null {
    if (isSketchpad(cubeId)) {
        return getSketchpadData(cubeId);
    }
    else if (isShelf(cubeId) || isFreezer(cubeId)) {
        return getShelfData(cubeId);
    }
    else if (isPartition(cubeId)) {
        return getPartitionData(cubeId);
    }
    else if (isGoods(cubeId)) {
        return getGoodsData(cubeId);
    }
    else if (isTextarea(cubeId)) {
        return getTextareaData(cubeId);
    }
    else if (isBafflePlate(cubeId)) {
        return getBaffleplateData(cubeId);
    }

    return null;
}

export {
    getCubeTreeData
};
